import { v4 as uuidv4 } from "uuid";
import { useCallback, useEffect, useRef } from "react";

import {
  actionNames,
  actionPrefixes,
  eventTypes,
  objectTypes,
  useTracker,
  type DimensionPropertyNames,
} from "@newsela/snitch";
import {
  HOMEPAGE_SESSION_KEY,
  LUNA_SESSION_KEY,
  SEARCH_SESSION_KEY,
  SEARCH_SESSION_QUERY_KEY,
} from "@/base/NewselaGlobalLayoutV2/NavigationBar/shared/constants";

/**
 * A custom hook for initializing a new Snitch homepage session tracker.
 *
 * Called on mount of TeacherHomepage. This hook:
 * 1. Generates a new unique ID for the homepage session.
 * 2. Persists the ID to `window.sessionStorage`.
 * 3. Updates Snitch's homepage session ID (auto-attached to all subsequent events).
 * 4. Fires a Snitch event indicating a homepage session has begun.
 *
 * A new session ID is generated on each homepage visit, so each discrete
 * visit is independently attributable in BigQuery.
 *
 * It also ends any search session, so homepage events aren't attributed to a
 * search the user already left.
 */
export function useHomepageSessionTracker() {
  const { trackAction, setHomepageSessionId, setSearchSessionId } =
    useTracker();

  useEffect(() => {
    const newHomepageSessionId = uuidv4();
    window.sessionStorage.setItem(HOMEPAGE_SESSION_KEY, newHomepageSessionId);
    setHomepageSessionId(newHomepageSessionId);
    window.sessionStorage.removeItem(SEARCH_SESSION_KEY);
    window.sessionStorage.removeItem(SEARCH_SESSION_QUERY_KEY);
    setSearchSessionId(null);

    trackAction({
      event_name: "Homepage Initiate Session",
      event_type: eventTypes.nativeInteraction,
      dimension_properties: {
        description: "Homepage session started",
        homepage_session_id: newHomepageSessionId,
        // `trackAction` still holds the previous search session id until the
        // state update commits; a caller-supplied value wins over it.
        ...({
          search_session_id: undefined,
        } as unknown as DimensionPropertyNames),
      },
    });
  }, []);
}

/**
 * A custom hook for initializing a new Snitch search tracking session.
 *
 * When the returned function `startSearchTracker` is called, this
 * hook performs four main actions:
 * 1. Generates a new unique ID for the search session.
 * 2. Persists the ID to `window.sessionStorage`.
 * 3. Updates Snitch's search session ID.
 * 4. Fires a Snitch event indicating a search has been initiated.
 *
 * The query is persisted too, so the search page can tell whether it was
 * reached by a search that already started a session (see
 * `useSearchSessionForNeedle`).
 *
 * @param metadata - Optional metadata attached to the Snitch event.
 * @returns Object containing the `startSearchTracker` function.
 */
export function useNewSearchSessionTracker(metadata?: Record<string, unknown>) {
  const { trackAction, getEventName, setSearchSessionId } = useTracker();
  const startSearchTracker = useCallback(
    (searchInput: string) => {
      const queryTerm = searchInput.trim();
      const newSearchSessionId = uuidv4();
      window.sessionStorage.setItem(SEARCH_SESSION_KEY, newSearchSessionId);
      window.sessionStorage.setItem(SEARCH_SESSION_QUERY_KEY, queryTerm);
      setSearchSessionId(newSearchSessionId);

      trackAction({
        event_name: getEventName(
          objectTypes.content,
          actionPrefixes.initiate,
          actionNames.search,
        ),
        event_type: eventTypes.nativeInteraction,
        dimension_properties: {
          description: "Search Session Started",
          search_session_id: newSearchSessionId,
          // TODO(DISCO-2758): Rattle's search fact reads `search_term` (MLAI-1276).
          // Drop it only after Rattle reads `query_term` too.
          search_term: searchInput,
          // TODO(DISCO-2710): drop the cast with the snitch upgrade; 1.17.2
          // has no `query_term`.
          ...({ query_term: queryTerm } as unknown as DimensionPropertyNames),
          ...(metadata ? { metadata } : null),
        },
      });
    },
    [trackAction, getEventName, setSearchSessionId],
  );
  return { startSearchTracker };
}

/**
 * Starts a search session on the search page when it was reached without one
 * for the current needle: deep links, back/forward, or a pasted URL.
 *
 * Searches from the nav bar or a suggestion already started a session for
 * this needle before navigating, so the event keeps the page the search was
 * made on as its `current_url`, and no second event fires here.
 */
export function useSearchSessionForNeedle(needle: string | undefined) {
  const { startSearchTracker } = useNewSearchSessionTracker();
  // `startSearchTracker` changes identity whenever a search session starts,
  // which can be before the needle has updated. Running the effect on that
  // change would start a session for the previous needle.
  const startSearchTrackerRef = useRef(startSearchTracker);
  startSearchTrackerRef.current = startSearchTracker;

  useEffect(() => {
    const queryTerm = needle?.trim();
    if (!queryTerm) return;
    if (window.sessionStorage.getItem(SEARCH_SESSION_QUERY_KEY) === queryTerm) {
      return;
    }
    startSearchTrackerRef.current(queryTerm);
  }, [needle]);
}

/**
 * Reads the Luna session id `startLunaSession` persisted, or `null` when there
 * is none.
 *
 * Guarded for the server the same way Snitch's own `getLunaSessionId` is, since
 * callers read it from `useRef`/`useState` initializers that also run during
 * SSR.
 */
export function getStoredLunaSessionId(): string | null {
  if (typeof globalThis.window === "undefined") return null;
  return window.sessionStorage.getItem(LUNA_SESSION_KEY);
}

/**
 * The moments of a Luna error worth counting, mapped to the constant
 * description each fires with.
 *
 * `event_name` cannot discriminate these — every Luna event collapses to
 * `Luna Error` — so the description is what dashboards group by, and it stays a
 * fixed string per kind rather than being interpolated.
 *
 * `retry` is the only in-notice recovery: a start-fresh error offers no button,
 * and the header's Start-new control tracks itself through `withTrackEvent`.
 */
const LUNA_ERROR_DESCRIPTIONS = {
  surfaced: "Luna surfaced an error",
  retry: "User retried a failed Luna turn",
  dismissed: "User dismissed a Luna error",
} as const;

export type LunaErrorEventKind = keyof typeof LUNA_ERROR_DESCRIPTIONS;

/**
 * A custom hook for initializing a new Snitch Luna session tracker.
 *
 * When the returned `startLunaSession` is called, this hook:
 * 1. Generates a new unique ID for the Luna session.
 * 2. Persists the ID to `window.sessionStorage`.
 * 3. Updates Snitch's Luna session ID (auto-attached to all subsequent events).
 * 4. Fires a Snitch event indicating a Luna session has begun.
 *
 * Also exposes `trackMessageSent`, used to count the back-and-forth turns of a
 * Luna conversation, and `trackError`, used to count failures and how users
 * recover from them.
 *
 * @returns Object containing the `startLunaSession`, `clearLunaSession`,
 * `trackMessageSent` and `trackError` functions.
 */
export function useNewLunaSessionTracker() {
  const { trackAction, getEventName, setLunaSessionId } = useTracker();
  const startLunaSession = useCallback(() => {
    const newLunaSessionId = uuidv4();
    window.sessionStorage.setItem(LUNA_SESSION_KEY, newLunaSessionId);
    setLunaSessionId(newLunaSessionId);

    trackAction({
      /** Ask PM for specific event name */
      event_name: getEventName(
        objectTypes.luna,
        actionPrefixes.initiate,
        actionNames.create,
      ),
      event_type: eventTypes.nativeInteraction,
      dimension_properties: {
        description: "Luna Session Started",
        luna_session_id: newLunaSessionId,
      },
    });

    return newLunaSessionId;
  }, [trackAction, getEventName, setLunaSessionId]);

  const clearLunaSession = useCallback(() => {
    window.sessionStorage.removeItem(LUNA_SESSION_KEY);
    setLunaSessionId(null);
  }, [setLunaSessionId]);

  /**
   * Fires one event per message the user sends to Luna, so the number of
   * back-and-forth turns is queryable by counting events per `luna_session_id`.
   *
   * Deliberately carries no part of the message — not the text, not its length,
   * not the block contents. The description is a constant.
   *
   * @param lunaSessionId - Pass the id explicitly on the turn that mints the
   * session. `setLunaSessionId` is React state, so `trackAction` still reads the
   * previous (null) id from its render-time closure on that first turn; a
   * caller-supplied `dimension_properties` value wins over the session state.
   * @param turnKind - The kind of turn the user sent (a typed message, a content
   * selection, …), so typed messages stay separable from the turns the UI sends
   * on the user's behalf. A static discriminator, never user input.
   */
  const trackMessageSent = useCallback(
    (lunaSessionId?: string, turnKind?: string) => {
      trackAction({
        event_name: getEventName(objectTypes.luna, null, actionNames.interact),
        event_type: eventTypes.nativeInteraction,
        dimension_properties: {
          description: "User sent a message to Luna",
          ...(turnKind ? { metadata: { turn_kind: turnKind } } : null),
          ...(lunaSessionId ? { luna_session_id: lunaSessionId } : null),
        },
      });
    },
    [trackAction, getEventName],
  );

  /**
   * Fires one event per moment of a surfaced Luna error, so failure rate and
   * which recovery users reach for are queryable per `luna_session_id`.
   *
   * All four kinds share the `Luna Error` event name on purpose: it keeps them
   * out of the `Luna Interact` bucket that counts sent messages, so a retry
   * cannot inflate the turn count.
   *
   * No explicit `luna_session_id` parameter here, unlike `trackMessageSent`: an
   * error can only follow a turn that already minted the session, so Snitch's
   * state has committed by then and attaches the id itself.
   *
   * @param kind - Which moment this is. Carries the constant description.
   * @param errorCode - SETI's error code, so failures are sliceable by cause.
   * A fixed enum from the wire, never user input or a raw error message.
   */
  const trackError = useCallback(
    (kind: LunaErrorEventKind, errorCode: string) => {
      trackAction({
        event_name: getEventName(objectTypes.luna, null, actionNames.error),
        event_type: eventTypes.nativeInteraction,
        dimension_properties: {
          description: LUNA_ERROR_DESCRIPTIONS[kind],
          error_code: errorCode,
        },
      });
    },
    [trackAction, getEventName],
  );

  return { startLunaSession, clearLunaSession, trackMessageSent, trackError };
}
